import React, { useState, useEffect } from 'react';
import { CheckCircleIcon } from '../components/icons';
import { contactApi, courseV2Api } from '../services/api';
import { FaWhatsapp } from 'react-icons/fa';
import { CourseV2 } from '../types';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import CustomDropdown from '../components/CustomDropdown';

const ContactPage: React.FC = () => {
  const { colors } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    course: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [courses, setCourses] = useState<CourseV2[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoadingCourses(true);
        const response = await courseV2Api.getAll();
        setCourses(response.data.data.courses);
      } catch (err) {
        setCourses([]);
      } finally {
        setLoadingCourses(false);
      }
    };
    fetchCourses();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const [touched, setTouched] = useState({ name: false, course: false });
  const isNameValid = formData.name.trim().length > 0;
  const isCourseValid = formData.course.trim().length > 0;
  const isFormValid = isNameValid && isCourseValid;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!isFormValid) return;
    try {
      await contactApi.sendContact(formData);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', mobile: '', course: '', message: '' });
      setTimeout(() => {
        setIsSubmitted(false);
        navigate('/');
      }, 2000);
    } catch (err: any) {
      setError('Failed to send message. Please try again later.');
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-2xl mx-auto p-8 sm:p-10 rounded-xl shadow-2xl" style={{ backgroundColor: colors.background.paper, boxShadow: `0 20px 50px -10px ${colors.accent.mint}30` }}>
        <div className="flex justify-center mb-6">
          <a
            href="https://wa.me/919779871610?text=Hi%2C%20I%20would%20like%20to%20know%20more%20about%20StudyRoom."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-white font-semibold py-2 px-4 rounded-lg text-lg transition-all duration-300 shadow-lg hover:scale-105"
            style={{
              background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `linear-gradient(135deg, ${colors.accent.mint} 0%, ${colors.primary.main} 100%)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`;
            }}
          >
            <FaWhatsapp className="w-6 h-6 mr-2" />
            Chat on WhatsApp
          </a>
        </div>
        <div className="flex items-center mb-6">
          <div className="flex-grow border-t border-neutral-600"></div>
          <span className="mx-4 text-neutral-400 font-semibold">OR</span>
          <div className="flex-grow border-t border-neutral-600"></div>
        </div>
        {isSubmitted && (
          <div className="mb-6 p-4 rounded-lg flex items-center space-x-3" style={{ backgroundColor: `${colors.accent.mint}20`, color: colors.accent.mint }}>
            <CheckCircleIcon className="w-6 h-6" />
            <p>Thank you! Your message has been "sent". We'll be in touch (not really, this is a demo).</p>
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-neutral-light mb-1">Full Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              name="name"
              id="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg transition-colors"
              style={{
                backgroundColor: colors.background.default,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: touched.name && !isNameValid ? colors.error : colors.border,
                color: colors.text.primary
              }}
              onFocus={() => setTouched({ ...touched, name: true })}
              onBlur={() => setTouched({ ...touched, name: true })}
            />
            {touched.name && !isNameValid && <div className="text-xs mt-1" style={{ color: colors.error }}>Name is required.</div>}
          </div>
          <div>
            <label htmlFor="course" className="block text-sm font-medium mb-1" style={{ color: colors.text.primary }}>Course Interested In <span style={{ color: colors.error }}>*</span></label>
            <CustomDropdown
              options={courses.map(course => ({ value: course.title, label: course.title }))}
              value={formData.course}
              onChange={(value) => setFormData({ ...formData, course: value })}
              placeholder={loadingCourses ? 'Loading courses...' : 'Select a course'}
              error={touched.course && !isCourseValid}
              disabled={loadingCourses}
              onFocus={() => setTouched({ ...touched, course: true })}
              onBlur={() => setTouched({ ...touched, course: true })}
            />
            {touched.course && !isCourseValid && <div className="text-xs mt-1" style={{ color: colors.error }}>Course is required.</div>}
          </div>
          <div>
            <label htmlFor="mobile" className="block text-sm font-medium mb-1" style={{ color: colors.text.primary }}>Mobile Number (optional)</label>
            <input
              type="text"
              name="mobile"
              id="mobile"
              value={formData.mobile}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-neutral-darker border border-neutral-DEFAULT rounded-lg text-neutral-light focus:ring-primary focus:border-primary transition-colors"
              placeholder="e.g. +919000000000"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-neutral-light mb-1">Email Address (optional)</label>
            <input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-neutral-darker border border-neutral-DEFAULT rounded-lg text-neutral-light focus:ring-primary focus:border-primary transition-colors"
              placeholder="e.g. you@email.com"
            />
          </div>
          <div>
            <label htmlFor="message" className="block text-sm font-medium mb-1" style={{ color: colors.text.primary }}>Message</label>
            <textarea
              name="message"
              id="message"
              rows={5}
              value={formData.message}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg transition-colors"
              style={{
                backgroundColor: colors.background.default,
                border: `1px solid ${colors.border}`,
                color: colors.text.primary
              }}
            ></textarea>
          </div>
          <div>
            <button
              type="submit"
              className="w-full text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 text-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 disabled:opacity-60 hover:scale-105 transform"
              style={{
                background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
                opacity: isFormValid ? 1 : 0.6
              }}
              onMouseEnter={(e) => {
                if (isFormValid) {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${colors.accent.mint} 0%, ${colors.primary.main} 100%)`;
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`;
              }}
              disabled={!isFormValid}
            >
              Send Message
            </button>
          </div>
        </form>
      </div>
      <div className="mt-12 text-center">
        <h3 className="text-2xl font-semibold mb-4" style={{ color: colors.text.primary }}>Or Reach Us Directly</h3>
        <p style={{ color: colors.text.secondary }}>Email: <a href="mailto:thestudyroom11@gmail.com" className="hover:underline transition-colors" style={{ color: colors.accent.mint }}>thestudyroom11@gmail.com</a></p>
        <p style={{ color: colors.text.secondary }}>Phone: <a href="tel:+919779871610" className="hover:underline transition-colors" style={{ color: colors.accent.mint }}>+91 9779871610</a></p>
        {/* <p className="mt-2" style={{ color: colors.text.secondary }}>123 Learning Lane, Knowledge City, KS 67890</p> */}
      </div>
    </div>
  );
};

export default ContactPage;
    